/**
 * React Query hooks for the Policy Intelligence Network.
 *
 * Provides data-fetching hooks for the dashboard, effectiveness,
 * recommendations, drift, and mutation hooks for accept/dismiss actions.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '../contexts/TenantContext';
import {
  policyIntelligenceApi,
  type PolicyIntelligenceSummary,
  type PolicyEffectivenessResult,
  type PolicyRecommendationsResult,
  type PolicyDriftResult,
  type BenchmarkResult,
  type PolicyIntelligenceSettings,
  type UpdatePolicyIntelligenceSettingsRequest,
} from '../services/policyIntelligenceApi';

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const policyIntelligenceKeys = {
  all: ['policyIntelligence'] as const,
  summary: () => [...policyIntelligenceKeys.all, 'summary'] as const,
  effectiveness: () => [...policyIntelligenceKeys.all, 'effectiveness'] as const,
  recommendations: () => [...policyIntelligenceKeys.all, 'recommendations'] as const,
  drift: () => [...policyIntelligenceKeys.all, 'drift'] as const,
  benchmarks: (scope?: string) => [...policyIntelligenceKeys.all, 'benchmarks', scope] as const,
  settings: () => [...policyIntelligenceKeys.all, 'settings'] as const,
};

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function usePolicyIntelligenceSummary() {
  const { isResolved } = useTenant();

  return useQuery<PolicyIntelligenceSummary>({
    queryKey: policyIntelligenceKeys.summary(),
    queryFn: () => policyIntelligenceApi.getSummary(),
    enabled: isResolved,
    staleTime: 60_000,
    retry: 1,
  });
}

export function usePolicyEffectiveness() {
  const { isResolved } = useTenant();

  return useQuery<PolicyEffectivenessResult>({
    queryKey: policyIntelligenceKeys.effectiveness(),
    queryFn: () => policyIntelligenceApi.getEffectiveness(),
    enabled: isResolved,
    staleTime: 60_000,
    retry: 1,
  });
}

export function usePolicyRecommendations() {
  const { isResolved } = useTenant();

  return useQuery<PolicyRecommendationsResult>({
    queryKey: policyIntelligenceKeys.recommendations(),
    queryFn: () => policyIntelligenceApi.getRecommendations(),
    enabled: isResolved,
    staleTime: 60_000,
    retry: 1,
  });
}

export function usePolicyDrift() {
  const { isResolved } = useTenant();

  return useQuery<PolicyDriftResult>({
    queryKey: policyIntelligenceKeys.drift(),
    queryFn: () => policyIntelligenceApi.getDrift(),
    enabled: isResolved,
    staleTime: 60_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useAcceptRecommendation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => policyIntelligenceApi.acceptRecommendation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyIntelligenceKeys.recommendations() });
      queryClient.invalidateQueries({ queryKey: policyIntelligenceKeys.summary() });
    },
  });
}

export function useDismissRecommendation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => policyIntelligenceApi.dismissRecommendation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyIntelligenceKeys.recommendations() });
      queryClient.invalidateQueries({ queryKey: policyIntelligenceKeys.summary() });
    },
  });
}

export function useDeferRecommendation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, deferUntil }: { id: string; deferUntil: string }) =>
      policyIntelligenceApi.deferRecommendation(id, deferUntil),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyIntelligenceKeys.recommendations() });
      queryClient.invalidateQueries({ queryKey: policyIntelligenceKeys.summary() });
    },
  });
}

// ---------------------------------------------------------------------------
// Benchmark hooks (F052)
// ---------------------------------------------------------------------------

export function useBenchmarkComparison(scope?: string) {
  const { isResolved } = useTenant();

  return useQuery<BenchmarkResult>({
    queryKey: policyIntelligenceKeys.benchmarks(scope),
    queryFn: () => policyIntelligenceApi.getBenchmarks(scope),
    enabled: isResolved,
    staleTime: 60_000,
    retry: 1,
  });
}

export function usePolicyIntelligenceSettings() {
  const { isResolved } = useTenant();

  return useQuery<PolicyIntelligenceSettings>({
    queryKey: policyIntelligenceKeys.settings(),
    queryFn: () => policyIntelligenceApi.getSettings(),
    enabled: isResolved,
    staleTime: 60_000,
    retry: 1,
  });
}

export function useUpdatePolicyIntelligenceSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdatePolicyIntelligenceSettingsRequest) =>
      policyIntelligenceApi.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyIntelligenceKeys.settings() });
      queryClient.invalidateQueries({ queryKey: policyIntelligenceKeys.benchmarks() });
    },
  });
}
